import { useState } from "react";
import axios from "axios";
import { X, UserPlus } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/roles";

export const InviteMemberModal = ({ onClose, onInvited, teams, assignableRoles, teamRequiredRoles, defaultTeamId }) => {
  const { API } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(assignableRoles.includes("user") ? "user" : assignableRoles[0] || "user");
  const [teamIds, setTeamIds] = useState(defaultTeamId ? [defaultTeamId] : []);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState("");

  const needsTeam = teamRequiredRoles.includes(role);
  const toggleTeam = (id) => setTeamIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const submit = async () => {
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    setInviting(true);
    setError("");
    try {
      await axios.post(`${API}/org/invitations`, { email: email.trim(), name: name.trim(), role, team_ids: teamIds }, { withCredentials: true });
      onInvited();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send the invitation.");
    } finally {
      setInviting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="invite-member-overlay">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="invite-member-modal">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Invite a Team Member</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-member-close-btn"
            className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10"
          >
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Full Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="invite-member-name-input"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-member-email-input"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="invite-member-role-select"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white"
            >
              {assignableRoles.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
              ))}
            </select>
          </div>
          {needsTeam && (
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Team (required for full access)</label>
              <div className="mt-1 flex flex-wrap gap-2" data-testid="invite-member-teams">
                {teams.length === 0 ? (
                  <p className="text-xs text-slate-400">No teams yet — create one first. The invite can still be sent without a team.</p>
                ) : (
                  teams.map((t) => (
                    <label key={t.team_id} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 dark:border-white/15 dark:text-slate-300">
                      <input type="checkbox" checked={teamIds.includes(t.team_id)} onChange={() => toggleTeam(t.team_id)} data-testid={`invite-member-team-${t.team_id}`} />
                      {t.name}
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        {needsTeam && teamIds.length === 0 && (
          <p className="mt-3 text-xs font-semibold text-amber-600 dark:text-amber-400" data-testid="invite-member-incomplete-notice">
            This role requires a team to unlock solution access. You can invite now and assign a team afterward.
          </p>
        )}
        {error && (
          <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="invite-member-error">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-member-cancel-btn"
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 transition-colors duration-200 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={inviting || !email.trim()}
            data-testid="invite-member-submit-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
          >
            <UserPlus size={15} /> {inviting ? "Sending…" : "Send Invite"}
          </button>
        </div>
      </div>
    </div>
  );
};
